// API URL configuration
// In production, REACT_APP_API_URL is not set (undefined), so we use empty string for same-origin
// In development, it defaults to localhost
const API_URL = process.env.REACT_APP_API_URL || '';

export async function fetchSheetTabs(sheetUrl) {
  const url = `${API_URL}/api/sheets/tabs?url=${encodeURIComponent(sheetUrl)}`;

  const response = await fetch(url, {
    credentials: 'include' // Include cookies for session
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Failed to fetch sheet tabs');
    error.needsAuth = data.needsAuth;
    error.needsSheetsScope = data.needsSheetsScope;
    throw error;
  }

  return data;
}

export async function fetchSheetData(sheetUrl, columnMapping = null, sheetName = null) {
  let url = `${API_URL}/api/sheets/data?url=${encodeURIComponent(sheetUrl)}`;

  if (columnMapping) {
    url += `&columnMapping=${encodeURIComponent(JSON.stringify(columnMapping))}`;
  }

  if (sheetName) {
    url += `&sheetName=${encodeURIComponent(sheetName)}`;
  }

  const response = await fetch(url, {
    credentials: 'include' // Include cookies for session
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Failed to fetch sheet data');
    error.needsAuth = data.needsAuth;
    error.needsSheetsScope = data.needsSheetsScope;
    error.needsMapping = data.needsMapping;
    error.headers = data.headers;
    throw error;
  }

  return data;
}

export async function syncToSheet(sheetUrl, markers, sheetName = null) {
  const response = await fetch(`${API_URL}/api/sheets/sync`, {
    method: 'POST',
    credentials: 'include', // Include cookies for session
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sheetUrl,
      markers,
      sheetName,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Failed to sync to sheet');
    error.needsAuth = data.needsAuth;
    error.needsSheetsScope = data.needsSheetsScope;
    throw error;
  }

  return data;
}

/**
 * @param {{ scopes?: string[] }} [options] - Optional extra scope keys for
 *   incremental auth (e.g. ['gmail'] for feedback send). Normal login omits this.
 */
export async function getAuthUrl(options = {}) {
  const params = new URLSearchParams();
  if (options.scopes?.length) {
    params.set('scopes', options.scopes.join(','));
  }
  const qs = params.toString();
  const response = await fetch(`${API_URL}/api/auth/url${qs ? `?${qs}` : ''}`, {
    credentials: 'include' // Include cookies for session
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to get auth URL');
  }

  return data;
}

export async function checkAuthStatus() {
  try {
    const response = await fetch(`${API_URL}/api/auth/status`, {
      credentials: 'include' // Include cookies for session
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return { authenticated: false };
  }
}

// Recent Sheets API
export async function getRecentSheets() {
  const response = await fetch(`${API_URL}/api/recent-sheets`, {
    credentials: 'include'
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch recent sheets');
  }

  return data;
}

export async function saveRecentSheet(sheetData) {
  const response = await fetch(`${API_URL}/api/recent-sheets`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sheetData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to save recent sheet');
  }

  return data;
}

export async function pinSheet(sheetData) {
  const response = await fetch(`${API_URL}/api/recent-sheets/pin`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sheetData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to pin sheet');
  }

  return data;
}

export async function unpinSheet(url, tabName) {
  const response = await fetch(`${API_URL}/api/recent-sheets/pin`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url, tabName }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to unpin sheet');
  }

  return data;
}

export async function deleteRecentSheet(url, tabName) {
  const response = await fetch(`${API_URL}/api/recent-sheets`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url, tabName }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete recent sheet');
  }

  return data;
}

export async function updateSheetName(url, tabName, newSheetName) {
  const response = await fetch(`${API_URL}/api/recent-sheets`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url, tabName, newSheetName }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update sheet name');
  }

  return data;
}

// QuickBase API
export async function fetchQuickbaseReport(reportUrl, columnMapping = null) {
  let url = `${API_URL}/api/quickbase/report?url=${encodeURIComponent(reportUrl)}`;

  if (columnMapping) {
    url += `&columnMapping=${encodeURIComponent(JSON.stringify(columnMapping))}`;
  }

  const response = await fetch(url, {
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Failed to fetch QuickBase report');
    error.needsMapping = data.needsMapping;
    error.headers = data.headers;
    error.data = data.data;
    throw error;
  }

  return data;
}

export async function syncToQuickbase(tableId, markers, fieldMap) {
  const response = await fetch(`${API_URL}/api/quickbase/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tableId,
      markers,
      fieldMap,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to sync to QuickBase');
  }

  return data;
}

// Recent QuickBase Reports API
export async function getRecentReports() {
  const response = await fetch(`${API_URL}/api/recent-reports`, {
    credentials: 'include'
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch recent reports');
  }

  return data;
}

export async function saveRecentReport(reportData) {
  const response = await fetch(`${API_URL}/api/recent-reports`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reportData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to save recent report');
  }

  return data;
}

export async function pinReport(reportData) {
  const response = await fetch(`${API_URL}/api/recent-reports/pin`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reportData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to pin report');
  }

  return data;
}

export async function unpinReport(url) {
  const response = await fetch(`${API_URL}/api/recent-reports/pin`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to unpin report');
  }

  return data;
}

export async function deleteRecentReport(url) {
  const response = await fetch(`${API_URL}/api/recent-reports`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete recent report');
  }

  return data;
}

export async function updateReportName(url, newReportName) {
  const response = await fetch(`${API_URL}/api/recent-reports`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url, newReportName }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update report name');
  }

  return data;
}

// Shared Maps API
export async function createSharedMap(mapData) {
  const response = await fetch(`${API_URL}/api/shared-maps`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mapData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create shared map');
  }

  return data;
}

export async function getSharedMap(shareId) {
  const response = await fetch(`${API_URL}/api/shared-maps/${shareId}`, {
    method: 'GET',
    credentials: 'include',
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to load shared map');
  }

  return data;
}

// ==========================================
// Coordinator Map API
// ==========================================

export async function fetchOpenMessageCounts(forceRefresh = false) {
  const url = forceRefresh
    ? `${API_URL}/api/coordinator-map/message-counts?refresh=true`
    : `${API_URL}/api/coordinator-map/message-counts`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load message counts');
  return data;
}

export async function fetchTicketMessages(recordId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/messages`, {
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load messages');
  return data;
}

export async function createTicketMessage(recordId, { text, type }) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, type }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create message');
  return data;
}

export async function replyToTicketMessage(recordId, messageId, text) {
  const response = await fetch(
    `${API_URL}/api/coordinator-map/tickets/${recordId}/messages/${messageId}/reply`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to send reply');
  return data;
}

export async function previewMessageApproval(recordId, messageId) {
  const response = await fetch(
    `${API_URL}/api/coordinator-map/tickets/${recordId}/messages/${messageId}/approve-preview`,
    { credentials: 'include' }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to review request');
  return data;
}

export async function approveTicketMessage(recordId, messageId) {
  const response = await fetch(
    `${API_URL}/api/coordinator-map/tickets/${recordId}/messages/${messageId}/approve`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to approve request');
  return data;
}

export async function closeTicketMessage(recordId, messageId) {
  const response = await fetch(
    `${API_URL}/api/coordinator-map/tickets/${recordId}/messages/${messageId}/close`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to close message');
  return data;
}

export async function fetchCoordinatorTickets(forceRefresh = false) {
  const url = forceRefresh
    ? `${API_URL}/api/coordinator-map/tickets?refresh=true`
    : `${API_URL}/api/coordinator-map/tickets`;
  const response = await fetch(url, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch tickets');
  return data;
}

export async function fetchCacheStatus(active = false) {
  const url = active
    ? `${API_URL}/api/coordinator-map/cache-status?active=true`
    : `${API_URL}/api/coordinator-map/cache-status`;
  const response = await fetch(url, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch cache status');
  return data;
}

export async function fetchCoordinatorFieldReps() {
  const response = await fetch(`${API_URL}/api/coordinator-map/field-reps`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch field reps');
  return data;
}

/**
 * Distinct field-rep IDs who completed Site Visits on a project.
 * @param {{ projectId: number, from?: string|null, to?: string|null }} params
 *   from/to optional YYYY-MM-DD; omit either for an open bound, both for all-time.
 */
export async function fetchRepsByCompletedProject({ projectId, from, to }) {
  const qs = new URLSearchParams({ projectId: String(projectId) });
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const response = await fetch(
    `${API_URL}/api/coordinator-map/reps-by-completed-project?${qs}`,
    { credentials: 'include' }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to query completed project history');
  return data; // { projectId, from, to, repIds, totalVisits }
}

/** Fresh project list from the QB Projects table. */
export async function fetchCoordinatorProjects() {
  const response = await fetch(`${API_URL}/api/coordinator-map/projects`, {
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch projects');
  return data; // { projects: [{ id, name, status }], totalRecords }
}

export async function refreshCoordinatorFieldReps() {
  const response = await fetch(`${API_URL}/api/coordinator-map/field-reps/refresh`, {
    method: 'POST',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to refresh field reps');
  return data;
}

export async function fetchCoordinatorLiveReps() {
  const response = await fetch(`${API_URL}/api/coordinator-map/live-reps`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch live reps');
  return data; // { liveReps: [...], enabled: bool }
}

/**
 * Save ticket changes to QuickBase.
 * On 409 hard conflict, throws a SaveConflictError with `.conflict = true` and
 * the diff details so the UI can render the conflict review panel.
 * On 422 dispatched-rejection, throws a SaveDispatchedError.
 */
export async function saveTicketChanges(recordId, changes, originalValues, knownDateModified, force = false) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/save`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ changes, originalValues, knownDateModified, force }),
  });
  const data = await response.json();

  if (response.status === 409 && data.hardConflict) {
    const err = new Error('Ticket conflict — your changes overlap with another edit.');
    err.hardConflict = true;
    err.lastModifiedBy = data.lastModifiedBy;
    err.lastModifiedAt = data.lastModifiedAt;
    err.conflictedFields = data.conflictedFields;
    throw err;
  }
  if (response.status === 422 && data.rejected === 'ticket-is-dispatched') {
    const err = new Error(data.message || 'Ticket is dispatched.');
    err.dispatched = true;
    err.blockedFields = data.blockedFields;
    throw err;
  }
  if (!response.ok) {
    throw new Error(data.error || `Save failed (${response.status})`);
  }

  return data; // { saved: true, ticket, softWarning? }
}

export async function fetchCoordinatorIcons() {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch icons');
  return data;
}

export async function fetchMapConfig() {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/map-config`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch map config');
  return data;
}

export async function updateMapConfig(config) {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/map-config`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update map config');
  return data;
}

/**
 * Trigger creation of a Field Nation workorder for this ticket. The backend
 * sets the QB "Create FN Ticket" flag and polls for the resulting Platform
 * Link for up to 15s. Returns { ok: true, ticket } when the link populates,
 * or { ok: true, pending: true, ticket } when it didn't populate in time.
 */
export async function createFnWorkorder(recordId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/create-fn-workorder`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Create FN workorder failed');
  return data;
}

/**
 * Fetch all confirmations linked to a ticket.
 * Returns { confirmations: [{ recordId, dateCreated, type, notes,
 *   confirmedTime, confirmedBy }] }
 */
export async function fetchTicketConfirmations(recordId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/confirmations`, {
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch confirmations');
  return data;
}

/**
 * Create a new confirmation on this ticket.
 * Body: { type, notes, confirmedTime? }
 * Returns: { ok: true, confirmation }
 */
export async function addTicketConfirmation(recordId, { type, notes, confirmedTime }) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/confirmations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, notes, confirmedTime }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to add confirmation');
  return data;
}

/**
 * Dispatch or recall a ticket. Returns { ok: true, ticket } with the
 * post-webhook ticket fields (dateModified, status, activeDsps) patched.
 */
export async function dispatchTicketAction(recordId, action) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/action`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || `${action} failed`);
  return data;
}

/** Pause between QB dispatch/recall webhooks so the pipeline is not flooded. */
export const WEBHOOK_STAGGER_MS = 500;

/**
 * Dispatch or recall many tickets one-by-one with a short delay between
 * webhook calls. Continues after individual failures.
 * onProgress({ index, total, recordId }) fires before each request.
 */
export async function dispatchTicketActions(recordIds, action, { onProgress, onTicketUpdated } = {}) {
  const results = [];
  for (let i = 0; i < recordIds.length; i++) {
    if (i > 0) {
      await new Promise(resolve => setTimeout(resolve, WEBHOOK_STAGGER_MS));
    }
    const recordId = recordIds[i];
    onProgress?.({ index: i, total: recordIds.length, recordId });
    try {
      const data = await dispatchTicketAction(recordId, action);
      if (data.ticket) onTicketUpdated?.(data.ticket);
      results.push({ recordId, ok: true, ticket: data.ticket });
    } catch (err) {
      results.push({ recordId, ok: false, error: err.message || `${action} failed` });
    }
  }
  return results;
}

export async function sendTicketOpportunity(recordId, repId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/send-opportunity`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ repId }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Send opportunity failed');
  return data;
}

export async function fetchOpportunityExtras(recordId, repIds = []) {
  const qs = repIds.length ? `?repIds=${repIds.join(',')}` : '';
  const response = await fetch(
    `${API_URL}/api/coordinator-map/tickets/${recordId}/opportunity-extras${qs}`,
    { credentials: 'include' }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load opportunity extras');
  return data;
}

export async function sendTicketReminder(recordId, repId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/send-reminder`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ repId }),
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.error || 'Send reminder failed');
    err.cooldownLeftMs = data.cooldownLeftMs;
    throw err;
  }
  return data;
}

export async function updateRepRateOverrides(recordId, { repRateOverrideList, overrideRepPay }) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/rep-rate-overrides`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ repRateOverrideList, overrideRepPay }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update rep rate overrides');
  return data;
}

export async function sendManualInvite(recordId, repId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/tickets/${recordId}/manual-invite`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ repId }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Manual invite failed');
  return data;
}

export async function fetchEditAccess() {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/edit-access`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch edit-access settings');
  return data;
}

export async function updateEditAccess({ mode, allowlist }) {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/edit-access`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode, allowlist })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update edit-access settings');
  return data;
}

export async function fetchApiUsage() {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/api-usage`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch API usage');
  return data;
}

export async function checkCoordinatorAdmin() {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/admin-check`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to check admin status');
  return data;
}

// Admin management
export async function fetchCoordinatorAdmins() {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/admins`, {
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch admins');
  return data;
}

export async function addCoordinatorAdmin(email) {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/admins`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to add admin');
  return data;
}

export async function removeCoordinatorAdmin(email) {
  const response = await fetch(`${API_URL}/api/coordinator-map/settings/admins/${encodeURIComponent(email)}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to remove admin');
  return data;
}

// Icon management
export async function uploadCoordinatorIcon(slug, file) {
  const formData = new FormData();
  formData.append('icon', file);
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to upload icon');
  return data;
}

export async function deleteCoordinatorIcon(slug) {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete icon');
  return data;
}

// Clear a type's override icon (keeps the type; reverts to the inherited item icon)
export async function deleteTypeIcon(slug) {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}/icon`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to clear icon');
  return data;
}

export async function updateIconVisibility(slug, visible) {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}/visibility`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visible })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update visibility');
  return data;
}

export async function uploadDefaultIcon(file) {
  const formData = new FormData();
  formData.append('icon', file);
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/default`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to upload default icon');
  return data;
}

export async function deleteDefaultIcon() {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/default`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete default icon');
  return data;
}

// Subtype rules
export async function addSubtypeRule(slug, rule) {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}/subtypes`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rule)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to add subtype rule');
  return data;
}

export async function updateSubtypeRule(slug, subtypeId, updates) {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}/subtypes/${subtypeId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update subtype rule');
  return data;
}

export async function deleteSubtypeRule(slug, subtypeId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}/subtypes/${subtypeId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete subtype rule');
  return data;
}

export async function uploadSubtypeIcon(slug, subtypeId, file) {
  const formData = new FormData();
  formData.append('icon', file);
  const response = await fetch(`${API_URL}/api/coordinator-map/icons/${encodeURIComponent(slug)}/subtypes/${subtypeId}/icon`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to upload subtype icon');
  return data;
}

// Item name group icons
export async function uploadItemIcon(slug, file) {
  const formData = new FormData();
  formData.append('icon', file);
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/icon`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to upload item icon');
  return data;
}

export async function deleteItemIcon(slug) {
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/icon`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete item icon');
  return data;
}

export async function addItemSubtypeRule(slug, rule) {
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/subtypes`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rule)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to add item subtype rule');
  return data;
}

export async function updateItemSubtypeRule(slug, subtypeId, updates) {
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/subtypes/${subtypeId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update item subtype rule');
  return data;
}

export async function deleteItemSubtypeRule(slug, subtypeId) {
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/subtypes/${subtypeId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete item subtype rule');
  return data;
}

export async function uploadItemSubtypeIcon(slug, subtypeId, file) {
  const formData = new FormData();
  formData.append('icon', file);
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/subtypes/${subtypeId}/icon`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to upload item subtype icon');
  return data;
}

export async function updateItemVisibility(slug, visible) {
  const response = await fetch(`${API_URL}/api/coordinator-map/items/${encodeURIComponent(slug)}/visibility`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visible })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update item visibility');
  return data;
}

/**
 * Submit an issue report or suggestion. Emails john@siteservicesnow.com via the
 * signed-in user's Gmail (requires gmail.send scope — re-auth if missing).
 */
export async function submitFeedback({ message, category, appPath, appLabel }) {
  const response = await fetch(`${API_URL}/api/feedback`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      category,
      appPath,
      appLabel,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    }),
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.error || 'Failed to send feedback');
    err.needsReauth = !!data.needsReauth;
    err.needsGmailScope = !!data.needsGmailScope;
    throw err;
  }
  return data;
}
